import React, { useEffect, useRef, useState } from "react";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import {
  attachCustomerLabel,
  getCustomerLabelsResult,
  previewCustomerLabels,
} from "api/shipment";

import { Button } from "components/Form";
import DefaultTable from "components/Table";

import { popupNotification, useLayoutDispatch } from "context/LayoutContext";

import { readShipmentSheet } from "utils/shipmentSheet";

import ShipmentImport, { getTrackingNumber } from "./ShipmentImport";

const POLL_INTERVAL = 3000;
const MAX_POLLS = 200; // ~10 minutes

const unmatchedHeaders = [
  { id: "file", label: "File" },
  { id: "page", label: "Page", render: (r) => r.page || "-" },
  { id: "reason", label: "Reason" },
  {
    id: "read",
    label: "Read",
    render: (r) => (r.read && r.read.length ? r.read.join(", ") : "-"),
  },
];

const waitingHeaders = [
  { id: "shipment_number", label: "Shipment" },
  {
    id: "tracking_numbers",
    label: "Tracking",
    render: (r) => r.tracking_numbers.join(", "),
  },
];

const isExcel = (file) => /\.xlsx?$/i.test(file.name);
const isPdf = (file) =>
  /\.pdf$/i.test(file.name) || file.type === "application/pdf";

const listText = (items, max = 8) =>
  items.slice(0, max).join(", ") +
  (items.length > max ? ` (+${items.length - max} more)` : "");

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const shipMethodOf = (row) => row["Ship Method"] || row["Ship Via"] || "";

/**
 * Problems the sheet alone shows, before the PDF is looked at. Rows without a tracking number or
 * with a ship method other than UPS are not handled here: they stop the import (see
 * handleFileChange).
 */
const checkSheet = (sheet) => {
  const errors = [];
  const warnings = [];

  const posByTracking = {};
  sheet.items.forEach((row) => {
    const tracking = getTrackingNumber(row);
    posByTracking[tracking] = posByTracking[tracking] || new Set();
    posByTracking[tracking].add(String(row["PO Number"]));
  });
  const shared = Object.entries(posByTracking)
    .filter(([, pos]) => pos.size > 1)
    .map(([tracking, pos]) => `${tracking} (POs ${[...pos].join(", ")})`);
  if (shared.length) {
    errors.push(
      `The same tracking number is on different POs: ${listText(shared, 5)}.`,
    );
  }

  if (sheet.skipped.length) {
    warnings.push(
      `${sheet.skipped.length} row(s) of the Excel were not read: ${listText(
        sheet.skipped.map(
          (s) => `row ${s.row} (missing ${s.missing.join(", ")})`,
        ),
      )}.`,
    );
  }

  return { errors, warnings };
};

/** What the import dialog shows above the table: the label PDF checked against the sheet. */
function LabelCheckPanel({ check, issues, trackingCount }) {
  const errors = [...issues.errors];
  const warnings = [...issues.warnings];

  if (check.missing.length) {
    errors.push(
      `${
        check.missing.length
      } tracking number(s) in the Excel have no label in the PDF: ${listText(
        check.missing,
      )}. Their shipments will wait for a label.`,
    );
  }
  if (check.used.length) {
    errors.push(
      `${
        check.used.length
      } tracking number(s) already belong to existing shipments and will be rejected: ${listText(
        check.used.map((u) => `${u.tracking_number} (${u.shipment_number})`),
        5,
      )}.`,
    );
  }
  if (check.unmatched.length) {
    warnings.push(
      `${check.unmatched.length} PDF page(s) could not be matched: ${listText(
        check.unmatched.map((u) => `p.${u.page || "-"} ${u.reason}`),
      )}.`,
    );
  }
  if (check.duplicates.length) {
    warnings.push(
      `${
        check.duplicates.length
      } tracking number(s) are on more than one page, only the first page is used: ${listText(
        check.duplicates.map(
          (d) => `${d.tracking_number} (${d.pages.join(", ")})`,
        ),
        5,
      )}.`,
    );
  }

  return (
    <div style={{ marginBottom: "1rem" }}>
      <Alert severity="info">
        {`PDF: ${check.pages} page(s). Labels found for ${check.matched.length} of ${trackingCount} tracking number(s).`}
      </Alert>
      {errors.map((message) => (
        <Alert severity="error" key={message} style={{ marginTop: "0.5rem" }}>
          {message}
        </Alert>
      ))}
      {warnings.map((message) => (
        <Alert severity="warning" key={message} style={{ marginTop: "0.5rem" }}>
          {message}
        </Alert>
      ))}
      {!errors.length && !warnings.length && (
        <Alert severity="success" style={{ marginTop: "0.5rem" }}>
          All labels were found.
        </Alert>
      )}
    </div>
  );
}

/**
 * Manual fallback for a label the import could not match: the PDF's pages go, in order, to this
 * shipment's lines still waiting for a label.
 */
export function AttachLabelButton({ shipmentId, onDone }) {
  const layoutDispatch = useLayoutDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setIsLoading(true);
    attachCustomerLabel({
      body: { shipment_id: shipmentId, file: await readFileAsBase64(file) },
      responseSetter: (res) => {
        setIsLoading(false);
        popupNotification({
          dispatch: layoutDispatch,
          message: res?.message || "Upload failed.",
          status: res?.success ? "success" : "error",
        });
        if (res?.success && onDone) onDone();
      },
    });
  };

  return (
    <Button color="blue" component="label" disabled={isLoading}>
      {isLoading ? "Uploading..." : "Upload Label"}
      <input
        hidden
        accept="application/pdf"
        type="file"
        onChange={handleFileChange}
      />
    </Button>
  );
}

function LabelReport({ report, onClose }) {
  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Quince Labels</DialogTitle>
      <DialogContent>
        <Alert
          severity={
            report.unmatched.length || report.waiting.length
              ? "warning"
              : "success"
          }
        >
          {`${report.matched.length} label(s) attached.`}
          {report.duplicates.length
            ? ` ${report.duplicates.length} already attached, skipped.`
            : ""}
        </Alert>
        {report.unmatched.length ? (
          <>
            <Typography variant="h6" style={{ marginTop: "1rem" }}>
              {`Pages not matched (${report.unmatched.length})`}
            </Typography>
            <DefaultTable
              headers={unmatchedHeaders}
              data={{ items: report.unmatched, isFetching: false }}
            />
          </>
        ) : null}
        {report.waiting.length ? (
          <>
            <Typography variant="h6" style={{ marginTop: "1rem" }}>
              {`Shipments still waiting for a label (${report.waiting.length})`}
            </Typography>
            <DefaultTable
              headers={waitingHeaders}
              data={{ items: report.waiting, isFetching: false }}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Quince labels: pick the order Excel (a UPS tracking number on every line) and the label PDF
 * together. The PDF is checked against the Excel first and the findings are shown in the import
 * dialog; once the shipments are created each PDF page is attached, by its tracking barcode, to the
 * shipment with that tracking. A final report lists the pages that could not be matched and the
 * shipments still waiting for a label.
 */
export default function QuinceLabelsButton({ onDone }) {
  const layoutDispatch = useLayoutDispatch();
  const [importData, setImportData] = useState(null);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const notify = (message, type = "error") =>
    popupNotification({ dispatch: layoutDispatch, message, status: type });

  const stopPolling = () => {
    clearInterval(pollRef.current);
    setStatus("");
  };

  // Waits for a background result; the backend writes it when the task is done.
  const waitForResult = (processKey, statusText, onResult) => {
    let polls = 0;
    setStatus(statusText);
    pollRef.current = setInterval(() => {
      polls += 1;
      if (polls > MAX_POLLS) {
        stopPolling();
        notify(
          "This is taking longer than expected, the report will come by email.",
          "info",
        );
        return;
      }
      getCustomerLabelsResult({
        processKey,
        responseSetter: (res) => {
          if (!res?.done) return;
          stopPolling();
          if (res.report.error) {
            notify(res.report.error);
            return;
          }
          onResult(res.report);
        },
      });
    }, POLL_INTERVAL);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    const excel = files.filter(isExcel);
    const pdf = files.filter(isPdf);
    if (files.length !== 2 || excel.length !== 1 || pdf.length !== 1) {
      notify("Please select one Excel file and one PDF file together.");
      return;
    }

    setStatus("Reading files...");
    const sheet = await readShipmentSheet(excel[0], getTrackingNumber);
    if (sheet.error) {
      setStatus("");
      notify(sheet.error);
      return;
    }
    const withoutTracking = sheet.items.filter(
      (row) => !getTrackingNumber(row),
    );
    if (withoutTracking.length) {
      setStatus("");
      const pos = [...new Set(withoutTracking.map((r) => r["PO Number"]))];
      notify(
        `Every row needs a UPS tracking number, nothing was imported. Missing on PO: ${listText(
          pos,
          10,
        )}`,
      );
      return;
    }
    // The labels are UPS, and loading checks the truck's carrier against the ship method: a row
    // with any other ship method could not be loaded on the UPS truck.
    const notUps = sheet.items.filter(
      (row) => !/^UPS/i.test(shipMethodOf(row)),
    );
    if (notUps.length) {
      setStatus("");
      const pos = [...new Set(notUps.map((r) => r["PO Number"]))];
      const methods = [...new Set(notUps.map((r) => shipMethodOf(r) || "-"))];
      notify(
        `Every row must ship with UPS, nothing was imported. Ship method ${listText(
          methods,
          4,
        )} on PO: ${listText(pos, 10)}`,
      );
      return;
    }

    const labelFile = {
      name: pdf[0].name,
      value: await readFileAsBase64(pdf[0]),
    };
    const trackings = [...new Set(sheet.items.map(getTrackingNumber))];
    previewCustomerLabels({
      body: { files: [labelFile], trackings },
      responseSetter: (res) => {
        if (!res?.success) {
          setStatus("");
          notify(res?.message || "The labels could not be checked.");
          return;
        }
        waitForResult(res.process_key, "Checking labels...", (check) =>
          setImportData({
            shipments: { count: sheet.items.length, items: sheet.items },
            duplicates: sheet.duplicates,
            labelFile,
            check,
            issues: checkSheet(sheet),
            trackingCount: trackings.length,
          }),
        );
      },
    });
  };

  const handleImported = ({ labelProcessKey }) => {
    if (labelProcessKey) {
      waitForResult(labelProcessKey, "Matching labels...", (result) => {
        setReport(result);
        if (onDone) onDone();
      });
    } else {
      // Large imports run in the background; the labels are matched right after it finishes.
      notify(
        "The labels are matched when the import finishes, the report comes by email.",
        "info",
      );
    }
  };

  return (
    <>
      <span title="Select the order Excel and the label PDF together">
        <Button
          variant="contained"
          color="primary-light"
          component="label"
          disabled={!!status}
        >
          {status || "Quince Labels"}
          <input
            hidden
            accept=".xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            type="file"
            multiple
            onChange={handleFileChange}
          />
        </Button>
      </span>
      {importData && (
        <ShipmentImport
          open={true}
          shipments={importData.shipments}
          duplicates={importData.duplicates}
          labelFiles={[importData.labelFile]}
          labelByTracking={Object.fromEntries(
            importData.check.matched.map((m) => [
              m.tracking_number,
              `p.${m.page}`,
            ]),
          )}
          notice={
            <LabelCheckPanel
              check={importData.check}
              issues={importData.issues}
              trackingCount={importData.trackingCount}
            />
          }
          onImported={handleImported}
          close={(imported) => {
            setImportData(null);
            if (imported && onDone) onDone();
          }}
        />
      )}
      {report && (
        <LabelReport report={report} onClose={() => setReport(null)} />
      )}
    </>
  );
}
